import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, KeyRound, Check, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Forgot-password flow rendered as a modal-style card.
 * Because we don't yet have an SMTP provider wired up, the backend returns
 * the reset token directly. We surface it to the user (or immediately let
 * them set a new password on the same screen).
 */
export const ForgotPasswordModal = ({ open, onClose }) => {
  const [step, setStep] = useState("identifier"); // identifier | reset | done
  const [identifier, setIdentifier] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { identifier });
      if (data.reset_token) {
        setToken(data.reset_token);
        setStep("reset");
        toast.success("Reset link ready — set a new password below.");
      } else {
        toast.info(data.message || "If an account exists, a reset link has been sent.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start password reset");
    }
    setLoading(false);
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      setStep("done");
      toast.success("Password updated. Please log in with the new password.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    }
    setLoading(false);
  };

  const close = () => {
    setStep("identifier");
    setIdentifier("");
    setToken("");
    setNewPassword("");
    onClose();
  };

  return (
    <div
      data-testid="forgot-password-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={close}
    >
      <div
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          data-testid="forgot-close-btn"
          className="absolute top-4 right-4 w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {step === "identifier" && (
          <>
            <div className="w-12 h-12 rounded-2xl bg-brand-gradient flex items-center justify-center text-white mb-5">
              <KeyRound size={22} />
            </div>
            <h2 className="font-display font-extrabold text-2xl tracking-tight">Reset your password</h2>
            <p className="text-gray-600 text-sm mt-2">
              Enter the email or mobile linked to your account. We&apos;ll help you set a new password.
            </p>
            <form onSubmit={requestReset} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-gray-500">Email or Mobile</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                  <Input
                    data-testid="forgot-identifier-input"
                    required
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10 h-12 rounded-xl"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="forgot-submit-btn"
                className="btn-primary w-full justify-center !py-3.5 !text-base"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : "Send reset link"}
              </button>
            </form>
          </>
        )}

        {step === "reset" && (
          <>
            <div className="w-12 h-12 rounded-2xl bg-brand-gradient flex items-center justify-center text-white mb-5">
              <KeyRound size={22} />
            </div>
            <h2 className="font-display font-extrabold text-2xl tracking-tight">Set a new password</h2>
            <p className="text-gray-600 text-sm mt-2">
              Choose a strong password of at least 6 characters.
            </p>
            <form onSubmit={submitReset} className="mt-6 space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-widest text-gray-500">New password</Label>
                <Input
                  data-testid="reset-password-input"
                  type="password"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="h-12 rounded-xl"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="btn-primary w-full justify-center !py-3.5 !text-base"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : "Update password"}
              </button>
            </form>
          </>
        )}

        {step === "done" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#3DDC97]/15 flex items-center justify-center text-[#0d8f5b] mb-5">
              <Check size={26} />
            </div>
            <h2 className="font-display font-extrabold text-2xl tracking-tight">Password updated</h2>
            <p className="text-gray-600 text-sm mt-2">
              Your password has been reset. Head back to the login screen to sign in with the new password.
            </p>
            <button
              onClick={close}
              data-testid="reset-done-btn"
              className="btn-primary w-full justify-center !py-3.5 !text-base mt-6"
            >
              Back to login
            </button>
          </>
        )}
      </div>
    </div>
  );
};
